/**
 * Tishya "Added to bag" modal.
 *
 * Opens after a successful add to cart on the product page, showing a
 * confirmation and the Pair with strip beneath it.
 *
 * Event contract: Horizon's product form dispatches `shopify:cart:lines-update`
 * on <product-form-component>, which bubbles to document. Captured empirically
 * from the running theme rather than assumed, because the constant lives in the
 * external @shopify/events package and is not readable from the theme source.
 *
 * The pairing swatches inside the modal are NOT handled here. The markup reuses
 * the `[data-tpair-card]` / `[data-tpair-swatch]` contract from the product page
 * strip, and tishya-pairing.js binds those with delegated listeners at document
 * level, so they already work.
 */
(() => {
  'use strict';

  const SESSION_KEY = 'tishya:addedModalCount';
  const CART_EVENT = 'shopify:cart:lines-update';

  const root = document.querySelector('[data-tam]');
  if (!root) return;

  const dialog = root.querySelector('[data-tam-dialog]');
  const maxPerSession = parseInt(root.dataset.tamMax || '3', 10);

  /** Element focus returns to on close, so keyboard users are not dumped at the top of the page. */
  let lastFocused = null;
  let open = false;

  /**
   * True only when this modal was the thing that locked scrolling, so closing
   * never unlocks a lock somebody else owns.
   *
   * Horizon locks the page with a `scroll-lock` attribute on <html>, backed by
   * `html[scroll-lock] { overflow: hidden }` in base.css and a reference counted
   * owner set in utilities.js. Setting our own inline overflow instead fought
   * that mechanism rather than joining it. utilities.js is an ES module and this
   * file is a plain script, so the attribute is used directly, guarded by this
   * flag to approximate the reference counting.
   */
  let ownsScrollLock = false;

  /* ---------------------------------------------------------------- helpers */

  const shownCount = () => {
    try {
      return parseInt(sessionStorage.getItem(SESSION_KEY) || '0', 10) || 0;
    } catch (_) {
      // Private mode can throw on sessionStorage. Fail open rather than break
      // add to cart: the modal simply stops being frequency capped.
      return 0;
    }
  };

  const bumpCount = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, String(shownCount() + 1));
    } catch (_) {
      /* no-op */
    }
  };

  const parseJSON = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch (_) {
      return fallback;
    }
  };

  /** Reads the colour currently checked in the main variant picker. */
  const selectedColour = () => {
    const input = document.querySelector('input[type="radio"][name^="Colour-"]:checked');
    return input ? input.value.trim() : null;
  };

  /**
   * Reads the selected size. Horizon renders size as radios like colour, but a
   * product with a single size may render none at all, so this can be null and
   * the row is hidden in that case.
   */
  const selectedSize = () => {
    const input = document.querySelector('input[type="radio"][name^="Size-"]:checked');
    return input ? input.value.trim() : null;
  };

  const focusable = () =>
    Array.from(
      dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);

  /* ------------------------------------------------------------ populating */

  const populate = () => {
    const colour = selectedColour();
    const size = selectedSize();

    if (colour) {
      const label = root.querySelector('[data-tam-colour]');
      if (label) label.textContent = colour;

      const thumb = root.querySelector('[data-tam-thumb]');
      const map = parseJSON(root.dataset.tamColours, {});
      if (thumb && map[colour]) {
        thumb.src = map[colour];
        thumb.srcset = '';
      }
    }

    // Size row is hidden rather than left empty when a product has no size.
    const sizeRow = root.querySelector('[data-tam-size-row]');
    const sizeValue = root.querySelector('[data-tam-size]');
    if (sizeRow && sizeValue) {
      if (size) {
        sizeValue.textContent = size;
        sizeRow.hidden = false;
      } else {
        sizeRow.hidden = true;
      }
    }
  };

  /* --------------------------------------------------------- open and close */

  const closeModal = () => {
    if (!open) return;
    open = false;

    root.hidden = true;

    if (ownsScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
      ownsScrollLock = false;
    }

    document.removeEventListener('keydown', onKeydown, true);

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  };

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeModal();
      return;
    }

    // Focus trap. Without this, tabbing walks out of the dialog and into the
    // page behind it, which screen reader users cannot recover from.
    if (event.key !== 'Tab') return;

    const items = focusable();
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const openModal = () => {
    if (open) return;
    if (shownCount() >= maxPerSession) return;

    lastFocused = document.activeElement;
    populate();

    root.hidden = false;
    open = true;
    bumpCount();

    // Lock the page behind the dialog so scrolling does not leak through.
    // Only claim the lock if nothing else already holds it.
    if (!document.documentElement.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
      ownsScrollLock = true;
    }

    document.addEventListener('keydown', onKeydown, true);

    // Focus the dialog itself rather than the first link, so a screen reader
    // announces the heading before any action.
    requestAnimationFrame(() => dialog.focus());
  };

  /* ------------------------------------------------------------- listeners */

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-tam-close]') || event.target.closest('[data-tam-scrim]')) {
      event.preventDefault();
      closeModal();
    }
  });

  document.addEventListener(CART_EVENT, (event) => {
    // Only the main product form on this page should trigger it. Quick add on
    // a card, and cart line quantity edits, dispatch the same event.
    const form = event.target && event.target.closest && event.target.closest('product-form-component');
    if (!form) return;
    if (form.closest('[data-tam]')) return;

    // Let the button's own confirmation state land first, so the two do not
    // animate over each other.
    setTimeout(openModal, 350);
  });
})();
